import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Project } from "../../api/models";
import { SessionAttachment } from "../../types/uploads";
import { AnswerLoading } from "../Answer";
import { UserChatMessage } from "../UserChatMessage";
import sharedStyles from "../EndReportFlow/EndReportFlow.module.css";

export type ReportMessage = {
    id: string;
    role: "ai" | "user";
    text: string;
    projects?: Project[];
    onProjectSelected?: (project: Project) => void;
    attachments?: SessionAttachment[];
    skipLabel?: string;
    onSkip?: () => void;
    download?: { blobUrl: string; filename: string };
    isLoading?: boolean;
    isError?: boolean;
    onRetry?: () => void;
};

interface Props {
    msg: ReportMessage;
}

export const ReportModeMessage = ({ msg }: Props) => {
    const { t } = useTranslation();

    if (msg.role === "user") {
        return <UserChatMessage message={msg.text} attachments={msg.attachments} />;
    }

    if (msg.isLoading) {
        return <AnswerLoading />;
    }

    const handleDownload = () => {
        if (!msg.download) return;
        const link = document.createElement("a");
        link.href = msg.download.blobUrl;
        link.download = msg.download.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className={sharedStyles.aiMessage}>
            {msg.text}
            {msg.projects && msg.projects.length > 0 && msg.onProjectSelected && (
                <div className={sharedStyles.projectChipsRow} style={{ marginTop: "0.75rem" }}>
                    {msg.projects.map(project => (
                        <button
                            key={project.id}
                            type="button"
                            className={sharedStyles.projectChip}
                            onClick={() => msg.onProjectSelected!(project)}
                        >
                            {project.name}
                        </button>
                    ))}
                </div>
            )}
            {msg.skipLabel && msg.onSkip && (
                <>
                    {" "}
                    <button type="button" className={sharedStyles.retryLink} onClick={msg.onSkip}>
                        {msg.skipLabel}
                    </button>
                </>
            )}
            {msg.download && (
                <>
                    {" "}
                    <button type="button" className={sharedStyles.downloadLink} onClick={handleDownload}>
                        <Download size={14} />
                        {t("reportMode.downloadLink", { filename: msg.download.filename })}
                    </button>
                </>
            )}
            {msg.isError && msg.onRetry && (
                <>
                    {" "}
                    <button type="button" className={sharedStyles.retryLink} onClick={msg.onRetry}>
                        {t("reportMode.retry")}
                    </button>
                </>
            )}
        </div>
    );
};
